import React, { useRef, useEffect, useState, useId } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../utils/graphUtils'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import type { ComparisonBarGraphThemeConfig } from './comparisonBarGraphThemes'

interface ComparisonBarGraphStyledProps {
  regularValue: number
  directValue: number
  theme: ComparisonBarGraphThemeConfig
  /** When 'sqrt', uses sqrt y-scale so the height difference between the two bars is visually compressed. */
  scaleType?: 'linear' | 'sqrt'
}

const STRIPE_PATTERN_SIZE = 12
const STRIPE_STROKE_WIDTH = 1

function addStripePattern(
  defs: d3.Selection<SVGDefsElement, unknown, null, undefined>,
  id: string,
  barColor: string,
  stripeColor: string
) {
  const pattern = defs
    .append('pattern')
    .attr('id', id)
    .attr('patternUnits', 'userSpaceOnUse')
    .attr('width', STRIPE_PATTERN_SIZE)
    .attr('height', STRIPE_PATTERN_SIZE)

  pattern.append('rect').attr('width', STRIPE_PATTERN_SIZE).attr('height', STRIPE_PATTERN_SIZE).attr('fill', barColor)

  // Single diagonal (top-right to bottom-left) corner to corner; thin stroke for finer stripes
  pattern
    .append('line')
    .attr('x1', STRIPE_PATTERN_SIZE)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', STRIPE_PATTERN_SIZE)
    .attr('stroke', stripeColor)
    .attr('stroke-width', STRIPE_STROKE_WIDTH)
}

const ComparisonBarGraphStyled: React.FC<ComparisonBarGraphStyledProps> = ({
  regularValue,
  directValue,
  theme,
  scaleType = 'linear',
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
  const regularPatternId = useId().replace(/:/g, '')
  const directPatternId = useId().replace(/:/g, '')

  useEffect(() => {
    const resizeListener = () => {
      if (svgRef.current) {
        const { width, height } = svgRef.current.getBoundingClientRect()
        setDimensions({ width, height })
      }
    }
    window.addEventListener('resize', resizeListener)
    resizeListener()
    return () => window.removeEventListener('resize', resizeListener)
  }, [])

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0) return

    let topTextHeight = 0
    let bottomTextHeight = 0

    let domainMin = Math.min(0, regularValue, directValue)
    let domainMax = Math.max(0, regularValue, directValue)

    const maxValue = Math.max(regularValue, directValue)
    const minValue = Math.min(regularValue, directValue)

    let origin = 0

    if (minValue > 0 && maxValue > 0) {
      domainMin = minValue * 0.8
      domainMax = maxValue
      origin = domainMin
      topTextHeight = 25
      bottomTextHeight = 55
    } else if (minValue < 0 && maxValue < 0) {
      domainMin = minValue
      domainMax = maxValue * 0.8
      origin = domainMax
      topTextHeight = 0
      bottomTextHeight = 85
    } else {
      domainMin = minValue * 1.2
      domainMax = maxValue
      origin = 0
      topTextHeight = 25
      bottomTextHeight = 80
    }

    const margin = { top: topTextHeight, right: 20, bottom: bottomTextHeight, left: 20 }
    const width = dimensions.width - margin.left - margin.right
    const height = dimensions.height - margin.top - margin.bottom

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)

    svg.selectAll('*').remove()

    const defs = svg.append('defs')
    if (theme.regular.stripeColor) {
      addStripePattern(defs, regularPatternId, theme.regular.barColor, theme.regular.stripeColor)
    }
    if (theme.direct.stripeColor) {
      addStripePattern(defs, directPatternId, theme.direct.barColor, theme.direct.stripeColor)
    }

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const barWidth = 60
    const gapBetweenBars = 60
    const totalWidth = 2 * barWidth + gapBetweenBars
    const startX = Math.max(0, (width - totalWidth) / 2)

    const yScaleLinear = d3.scaleLinear().domain([domainMin, domainMax]).range([height, 0])
    const y: (v: number) => number =
      scaleType === 'sqrt'
        ? (value) => {
            const t = (value - domainMin) / (domainMax - domainMin)
            const sqrtT = Math.sqrt(Math.max(0, Math.min(1, t)))
            return height * (1 - sqrtT)
          }
        : (value) => yScaleLinear(value)
    const y0 = y(origin)

    const radius = theme.topCornerRadius

    const drawBar = (
      value: number,
      x: number,
      barColor: string,
      patternId: string | null
    ) => {
      let barHeight: number
      let yPos: number
      let roundTop: boolean
      let roundBottom: boolean
      let labelY: number

      if (value >= 0) {
        yPos = y(value)
        barHeight = y0 - yPos
        roundTop = true
        roundBottom = false
        labelY = yPos - 10
      } else {
        yPos = y0
        barHeight = y(value) - y0
        roundTop = false
        roundBottom = true
        labelY = y(value) + 20
      }

      if (value !== 0 && barHeight < 2) {
        barHeight = 2
        if (value > 0) {
          yPos = y0 - barHeight
        }
      }

      const path = g
        .append('path')
        .attr(
          'd',
          rounded_rect(x, yPos, barWidth, barHeight, radius, roundTop, roundTop, roundBottom, roundBottom)
        )

      if (patternId) {
        path.attr('fill', `url(#${patternId})`)
      } else {
        path.attr('fill', barColor)
      }

      g.append('text')
        .attr('x', x + barWidth / 2)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .attr('fill', barColor)
        .text(`${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`)
    }

    drawBar(
      regularValue,
      startX,
      theme.regular.barColor,
      theme.regular.stripeColor ? regularPatternId : null
    )
    drawBar(
      directValue,
      startX + barWidth + gapBetweenBars,
      theme.direct.barColor,
      theme.direct.stripeColor ? directPatternId : null
    )

    const axisStroke = theme.axisLineStroke ?? '#E77464'
    g.append('line')
      .attr('x1', 0)
      .attr('y1', y0)
      .attr('x2', width)
      .attr('y2', y0)
      .attr('stroke', axisStroke)
      .attr('stroke-width', 0.5)
      .attr('opacity', 0.3)

    const addCategoryLabel = (labelX: number, color: string, textLines: string[]) => {
      const yPos =
        regularValue < 0 || directValue < 0 ? y(domainMin) + 45 : y0 + 25
      const text = g
        .append('text')
        .attr('x', labelX)
        .attr('y', yPos)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '400')
        .attr('fill', color)

      textLines.forEach((line, lineIndex) => {
        text
          .append('tspan')
          .attr('x', labelX)
          .attr('dy', lineIndex === 0 ? '0' : '1.2em')
          .text(line)
      })
    }

    addCategoryLabel(startX + barWidth / 2, theme.regular.barColor, theme.regular.labelLines)
    addCategoryLabel(
      startX + barWidth + gapBetweenBars + barWidth / 2,
      theme.direct.barColor,
      theme.direct.labelLines
    )
  }, [
    dimensions,
    regularValue,
    directValue,
    theme,
    scaleType,
    regularPatternId,
    directPatternId,
  ])

  return <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
}

export default ComparisonBarGraphStyled
